import { ConfigProvider, theme } from 'antd'
import { lazy, Suspense, useEffect, useState } from 'react'
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import './App.css'
import { useAppDispatch, useAppSelector } from './app/hooks'
import { checkDarkMode, selectDarkMode } from './app/reducers/global'
import { fetchUser } from './app/reducers/user'

// Google Analytics
import ReactGA from 'react-ga'
import { TRACKING_ID } from './Constants'

// Non-lazy loaded pages
import ErrorBoundary from './components/ErrorBoundary'
import Loader from './components/Loader'
import NotFound from './pages/NotFound'

// Lazily Loading other pages
const Home = lazy(() => import(/* webpackChunkName: "home-page" */ './pages/Home'))
const NavBar = lazy(() => import(/* webpackChunkName: "navbar" */ './components/NavBar'))
const Question = lazy(() => import(/* webpackChunkName: "question-page" */ './pages/Question'))
const ContestLanding = lazy(() => import(/* webpackChunkName: "contest-page" */ './pages/ContestLanding'))
const Community = lazy(() => import(/* webpackChunkName: "community-page" */ './pages/Community'))
const Store = lazy(() => import(/* webpackChunkName: "store-page" */ './pages/Store'))
const ComingSoon = lazy(() => import(/* webpackChunkName: "comingsoon-page" */ './pages/ComingSoon'))
const Profile = lazy(() => import(/* webpackChunkName: "profile-page" */ './pages/Profile'))
const LiveContest = lazy(() => import(/* webpackChunkName: "live-contest-page" */ './pages/LiveContest'))
const PricingPage = lazy(() => import(/* webpackChunkName: "pricing-page" */ './pages/Pricing'))

// Tracking
ReactGA.initialize(TRACKING_ID)

const App = () => {
    const dispatch = useAppDispatch()
    const isDarkMode = useAppSelector(selectDarkMode)
    const [themeMode, setThemeMode] = useState(isDarkMode ? 'dark' : 'light')

    useEffect(() => {
        dispatch(fetchUser())
        ReactGA.pageview(window.location.pathname + window.location.search)
        dispatch(checkDarkMode())
    }, [dispatch])

    useEffect(() => {
        setThemeMode(isDarkMode ? 'dark' : 'light')
        localStorage.setItem('darkMode', isDarkMode.toString())
    }, [isDarkMode])

    return (
        <ConfigProvider
            theme={{
                algorithm: themeMode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
            }}
        >
            <div className="App">
                <ErrorBoundary>
                    <Suspense fallback={<Loader />}>
                        <Router>
                            <NavBar />
                            <Routes>
                                <Route path="/question/:id" element={<Question />} />
                                <Route path="/contest" element={<ContestLanding />} />
                                <Route path="/community" element={<Community />} />
                                <Route path="/store" element={<Store />} />
                                <Route path="/profile" element={<Profile />} />
                                <Route path="/comingsoon" element={<ComingSoon />} />
                                <Route path="/livecontest/:id" element={<LiveContest />} />
                                <Route path="/pricing" element={<PricingPage />} />
                                <Route path="/" element={<Home />} />
                                <Route path="*" element={<NotFound />} />
                            </Routes>
                        </Router>
                    </Suspense>
                </ErrorBoundary>
            </div>
        </ConfigProvider>
    )
}

export default App
